// ============================================================================
// Aislamiento entre sesiones (porte de CC-CONSOLE: `queryClient.clear()` al
// cambiar de usuario)
// ============================================================================
// La caché de react-query y algunos stores viven en memoria mientras la pestaña
// siga abierta. Al cerrar sesión, o al entrar otra cuenta en el mismo
// navegador, lo de la sesión anterior (unidades, viajes, conversaciones del
// asistente, chat de soporte…) se podía pintar en la nueva hasta que cada
// consulta se refrescara. Aquí se vacía todo eso cuando cambia el usuario.
// ============================================================================
import { useEffect, useRef } from "react";
import { queryClient } from "./queryClient";
import { useUserStore } from "../store/userStore";
import { useAiChatStore } from "../store/aiChatStore";
import { useSupportChatStore } from "../store/supportChatStore";
import { useFeatureFlagsRuntimeStore } from "../store/featureFlagsRuntimeStore";

/** Vacía la caché de consultas y los stores con datos de la sesión. */
export function clearSessionData(): void {
  queryClient.clear();
  const steps: Array<() => void> = [
    () => useAiChatStore.getState().clearMessages(),
    () => useSupportChatStore.getState().reset(),
    () => useFeatureFlagsRuntimeStore.getState().reset(),
  ];
  for (const step of steps) {
    try {
      step();
    } catch (error) {
      // Un store que falle al limpiarse no deja a medias a los demás.
      console.warn("[sessionIsolation] no se pudo limpiar un store:", error);
    }
  }
}

/**
 * Vacía la caché cuando HABÍA una sesión y cambió el usuario (cierre de sesión
 * u otra cuenta). No hace nada en el arranque ni al entrar por primera vez.
 */
export function useClearOnUserChange(): void {
  const sessionUserId = useUserStore((state) => state.user?.id ?? state.user?.uid ?? null);
  const previousUserId = useRef<string | null>(null);
  useEffect(() => {
    const previous = previousUserId.current;
    previousUserId.current = sessionUserId;
    if (previous && previous !== sessionUserId) clearSessionData();
  }, [sessionUserId]);
}
